import { Component, OnInit } from '@angular/core';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AdminState } from '../../models/enums/adminState';
import { CommonModule } from '@angular/common';
import { SuperAdminService } from '../../services/super-admin/super-admin.service';
import { MessageService } from 'primeng/api';
import { ToggleButtonChangeEvent, ToggleButtonModule } from 'primeng/togglebutton';
import { DialogModule } from 'primeng/dialog';
import { Gender, GenderDescriptions } from '../../models/enums/enums';
import { DropdownModule } from 'primeng/dropdown';
import { SchoolResponse } from '../../models/schools/schoolResponse';
import { SchoolService } from '../../services/school/school.service';
import { ListSchoolRequest } from '../../models/schools/listSchoolRequest';
import { ListSchoolNameBySearchTextResponse } from '../../models/schools/listSchoolNameBySearchTextResponse';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { SchoolStatus } from '../../models/enums/schoolState';
import { RegisterSchoolRequest } from '../../models/schools/registerSchoolRequest';
import { countryResponse } from '../../models/country/countryResponse';
import { UtilityService } from '../../services/utility/utility.service';
import { UpdateSchoolRequest } from '../../models/schools/updateSchoolRequest';
import { SchoolAdminResponse } from '../../models/schools/schoolAdminResponse';
import { SchoolAdminStatus } from '../../models/enums/schoolAdminStatus';
import { passwordValidator } from '../../utility/passwordValidator';
import { RegisterSchoolAdminRequest } from '../../models/schools/registerSchoolAdminRequest';

interface DropdownOption {
  name: string;
  value: SchoolStatus;
}

@Component({
  selector: 'app-add-school',
  standalone: true,
  imports: [TableModule,ButtonModule,InputTextModule,PasswordModule, AutoCompleteModule,
    ReactiveFormsModule, FormsModule, CommonModule, DialogModule, DropdownModule, ToggleButtonModule, PasswordModule],
  templateUrl: './add-school.component.html',
  styleUrl: './add-school.component.scss'
})
export class AddSchoolComponent implements OnInit{

  totalRecords: number = 0;
  first: number = 0;
  rowSize: number = 10;
  pageSize = 10;
  schoolStateOptions: DropdownOption[] = [];
  schoolList: SchoolResponse[] = []
  schoolToEdit: SchoolResponse | null = null;
  adminToEdit: SchoolAdminResponse | null = null;
  schoolNames: ListSchoolNameBySearchTextResponse[] = [];
  schoolIdForAdmin!: number;
  readonly SchoolStatus = SchoolStatus;
  readonly SchoolAdminStatus = SchoolAdminStatus;

  newSchoolForm!: FormGroup;
  schoolAdminForm!: FormGroup;
  filterForm!: FormGroup;
  genders: { name: string; value: number }[] = [];
  countries: countryResponse[] = [];

  isLoading: boolean = false;
  isAddSchoolVisible: boolean = false;
  isAdminVisible: boolean = false;
  submitted: boolean = false;
  hidePasswordFields: boolean = false;

  initializeForm(){
    this.filterForm = this.formBuilder.group({
      schoolName: [null],
      schoolState: [null]
    });

    this.newSchoolForm = this.formBuilder.group({
      name: ['', Validators.required],
      address: [''],
      city:[''],
      state:[''],
      zipCode:['', Validators.pattern('^[1-9][0-9]{5}$')],
      country:[''],
      email: ['',  Validators.pattern('[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$')],
      phoneNumber: ['', Validators.pattern('^[0-9]{10}$')],
      website: ['', [Validators.pattern('https?://.+')]],
      // state: [null, Validators.required],
    });

    this.schoolAdminForm = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.pattern('[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$')]],
      newPassword: ['', [Validators.required, passwordValidator()]],
      confirmPassword: ['', Validators.required],
    }, { validators: this.passwordsMatchValidator });
  }

  passwordsMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    return newPassword === confirmPassword ? null : { passwordMismatch: true };
  }

  constructor(
      private schoolService: SchoolService,
      private messageService: MessageService,
      private formBuilder: FormBuilder,
      private utilityService: UtilityService,
    ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.populateDropdownFromEnum();
    this.getSchools(1);
    this.loadCountries();
    this.genders = [
      { name: 'Male', value: 1 },
      { name: 'Female', value: 2 },
      { name: 'Other', value: 3 }
    ]
  }

  onSchoolDialogVisibilityChange(visible: boolean) {
    if (!visible) {
      this.newSchoolForm.reset();
      this.schoolToEdit = null;
    }
  }
  onAdminDialogVisibilityChange(visible: boolean) {
    if (!visible) {
      this.schoolAdminForm.reset();
      this.schoolAdminForm.enable();
      this.hidePasswordFields = false;
      this.adminToEdit = null;
      this.submitted = false;
    }
  }

  private loadCountries(): void {
    this.utilityService.getCountries().subscribe({
      next: (response) => {
        if (response?.result) {
          this.countries = response.result;
        }
      },
      error: (err) => console.warn('Error loading countries:', err),
    });
  }

  // onStatusChange(schoolId: number){
  //   this.updateSchoolStatus(schoolId);
  // }

  populateDropdownFromEnum(){
    this.schoolStateOptions = Object.keys(SchoolStatus).filter(key => isNaN(Number(key))).map(key =>({
      name: key,
      value: SchoolStatus[key as keyof typeof SchoolStatus]
    }));
  }

  genderDescriptions(arg0: Gender | undefined): string {
    if (arg0) {
      return GenderDescriptions[arg0];
    }
    else {
      return '';
    }
  }

  filterSchoolNames(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 3) {
      this.schoolService.listSchoolNameBySearchText(event.query).subscribe({
        next: (response) => {
          this.schoolNames = response.result
        }
      });
    } else {
      this.schoolNames = [];
    }
  }

  private statusMap: { [key: string]: boolean | undefined } = {
    [SchoolStatus.Active]: true,
    [SchoolStatus.Inactive]: false,
  };

  updateSchoolStatus(schoolId: number){
    this.isLoading = true;
    this.schoolService.updateSchoolStatus(schoolId).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.getSchools(1);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'School status updated successfully' });
      },
      error:(err) =>{
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error updating school status' });
      }
    })
  }

  upsertSchool(){
    if(this.schoolToEdit){
      this.editSchool(this.schoolToEdit.id);
    }else{
      this.addSchool();
    }
  }

  editSchool(id: number){
    this.isLoading = true;
    this.submitted = true;
    const updateSchoolRequest: UpdateSchoolRequest = {
      name: this.newSchoolForm.value.name,
      address: this.newSchoolForm.value.address,
      city: this.newSchoolForm.value.city,
      state: this.newSchoolForm.value.state,
      zipCode: this.newSchoolForm.value.zipCode,
      country: this.newSchoolForm.value.country,
      phoneNumber: this.newSchoolForm.value.phoneNumber,
      email: this.newSchoolForm.value.email,
      website: this.newSchoolForm.value.website,
      schoolId: id
    }
    this.schoolService.updateSchoolDetails(updateSchoolRequest).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.submitted = false;
        this.closeDialog();
        this.newSchoolForm.reset();
        this.getSchools(1);
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'School details updated successfully' });
      },
      error:(err) =>{
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error updating admin' });
      }
    })
  }

  getSchools(pageNumber: number){
    this.isLoading = true;
    const schoolSearchRequest : ListSchoolRequest = {
      name: this.filterForm.value.schoolName,
      status: this.statusMap[this.filterForm.value.schoolState],
      pageSize: this.rowSize,
      pageNumber: pageNumber
    }
    this.schoolService.listSchools(schoolSearchRequest).subscribe({
      next: (response) => {
        if(response){
          this.schoolList = response.result;
          this.totalRecords = response.total;
          this.isLoading = false;
        }else{
          this.schoolList = [];
          this.totalRecords = 0;
          this.isLoading = false;
        }
      },
      error: (err) =>{
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error Fetching Schools' });
      }
    })
  }

  addSchool(){
    this.isLoading = true;
    this.submitted = true;
    const registerSchoolRequest: RegisterSchoolRequest = {
      name: this.newSchoolForm.value.name,
      address: this.newSchoolForm.value.address,
      city: this.newSchoolForm.value.city,
      state: this.newSchoolForm.value.state,
      zipCode: this.newSchoolForm.value.zipCode,
      country: this.newSchoolForm.value.country,
      phoneNumber: this.newSchoolForm.value.phoneNumber,
      email: this.newSchoolForm.value.email,
      website: this.newSchoolForm.value.website
    }
    this.schoolService.registerSchool(registerSchoolRequest).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.submitted = false;
        if(response){
          this.schoolList.push(response);
          this.newSchoolForm.reset();
          this.closeDialog();
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'School added successfully' });
        }else{
          this.isLoading = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error adding school' });
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error adding school' });
      }
    })
  }

  addSchoolAdmin(){
    this.isLoading = true;
    this.submitted = true;
    const registerSchoolAdminRequest: RegisterSchoolAdminRequest = {
      firstName: this.schoolAdminForm.value.firstName,
      lastName: this.schoolAdminForm.value.lastName,
      email: this.schoolAdminForm.value.email,
      password: this.schoolAdminForm.value.newPassword,
      confirmPassword: this.schoolAdminForm.value.confirmPassword,
      schoolId: this.schoolIdForAdmin
    }
    this.schoolService.registerSchoolAdmin(registerSchoolAdminRequest).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.submitted = false;
        this.closeDialog();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'School-Admin added successfully' });
      },
      error: (err) => {
        this.isLoading = false;

        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error adding school-admin' });
      }
    })
  }

  getSchoolAdmin(){
    this.isLoading = true;
    this.schoolService.getSchoolAdminById(this.schoolIdForAdmin).subscribe({
      next: (response) => {
        
        if(response){
          this.isLoading = false;
          this.adminToEdit = response;
          this.populateAdminForm();
          this.schoolAdminForm.disable();
          this.hidePasswordFields = true;
        }else{
          this.isLoading = false;
          this.schoolAdminForm.enable();
          this.hidePasswordFields = false;
        }
        
      },
      error: (error) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'warn', summary: 'Error', detail: 'No school-admin registered' });
      }
    })
  }

  // addSchoolDialog(){
  //   this.isVisible = true;
  //   if(school){
  //   this.schoolToEdit = school;
  //   this.populateEditForm();
  //   }else{
  //     this.schoolToEdit = null;
  //     this.newSchoolForm.reset();
  //   }
  // }
  private populateAdminForm(){
    if(this.adminToEdit){
      this.schoolAdminForm.patchValue({
        firstName: this.adminToEdit.firstName,
        lastName: this.adminToEdit.lastName,
        email: this.adminToEdit.email,
        newPassword: [null],
        confirmPassword: [null],
      })
    }
  }
  private populateEditSchoolForm(){
    if (this.schoolToEdit) {
      this.newSchoolForm.patchValue({
        name: this.schoolToEdit.name,
        address: this.schoolToEdit.address,
        city: this.schoolToEdit.city,
        state: this.schoolToEdit.state,
        zipCode: this.schoolToEdit.zipCode,
        country: this.schoolToEdit.country,
        phoneNumber: this.schoolToEdit.phoneNumber,
        email: this.schoolToEdit.email,
        website: this.schoolToEdit.website
      })
    }
  }

  addSchoolDialog(school?: SchoolResponse){
    if(school){
      this.schoolToEdit = school;
      this.populateEditSchoolForm();
      this.isAddSchoolVisible = true;
    }else{
      this.schoolToEdit = null;
      this.isAddSchoolVisible = true;
    }
  }
  
  adminDialog(schoolid: number){
    
    this.schoolIdForAdmin = schoolid;
    this.getSchoolAdmin();
    this.isAdminVisible = true;
  }
  
  closeDialog() {
    this.isAddSchoolVisible = false;
    this.isAdminVisible = false;
    this.newSchoolForm.reset();
    this.schoolAdminForm.reset();
    this.hidePasswordFields = false;
    this.schoolToEdit = null;
    this.adminToEdit = null;
  }

  onLazyLoad($event: TableLazyLoadEvent): void {
    const pageNumber = Math.floor(($event.first ?? 0) / ($event.rows ?? this.rowSize)) + 1;
    this.first = $event.first ?? 0;
    this.rowSize = $event.rows ?? this.rowSize;
    this.getSchools(pageNumber);
  }
}
